import React, { useCallback, useEffect, useMemo, useState } from 'react';

// DEPENDENCIES
import clsx from 'clsx';
import { useNavigate, useLocation } from 'react-router-dom';

// GLOBAL VARIABLES
import { ENDPOINTS } from 'endpoints.js';
import { INTERFACE, SETTINGS, AM_FINANCE } from 'pathnames';

// LOCAL VARIABLES
import { PRODUCTS_COLUMNS } from './List';

// FUNCTIONS
// eslint-disable-next-line no-unused-vars
import { apiFetch, makePath } from 'functions.js';

// GLOBAL COMPONENTS
import Button from 'core/tools/Button';
import Main from 'parts/main/Main';
import Table from 'parts/table/Table';
import Title from 'parts/title/Title';
import PageButtons from 'parts/page/parts/PageButtons';

// MAIN COMPONENT
const LenderOrder = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [order, setOrder] = useState([]);

  const { search } = useLocation();
  const urlParams = new URLSearchParams(search);
  const saleTypeCategoryId = urlParams.get('saleTypeCategoryId');

  const columns = useMemo(() => [
    { key: 'dealer_amfinance_id', label: '', show: false },
    { key: 'sale_type_category', label: 'Sale Type', width: 70 },
    ...PRODUCTS_COLUMNS
  ], []);

  // CALLBACKS
  const fetchRecords = useCallback(() => {
    apiFetch({
      endpoint: ENDPOINTS.admin.amFinance.products.list,
      params: {
        saleTypeCategoryId
      },
      loadingMessage: 'Fetching dealer finance products',
      errorMessage: 'Unable to fetch dealer finance products.',
      onFetch: () => setLoading(true),
      onSuccess: d => {
        const saleTypeCategory = d?.result?.[0]?.sale_type_category;
        const products = d?.result?.[0]?.products || [];
        const newProduct = products.map(v => ({ sale_type_category: saleTypeCategory, ...v }));
        setData(newProduct);
        setOrder(products.map(({ dealer_amfinance_id, sort_order }) => ({ dealer_amfinance_id, sort_order })));
      },
      onComplete: () => setLoading(false)
    });
  }, [saleTypeCategoryId]);

  // EFFECTS
  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const handleRowDrop = useCallback(({ records: products }) => {
    setOrder(products.map((p, i) => ({ dealer_amfinance_id: p.dealer_amfinance_id, sort_order: i + 1 })));
  }, []);

  // RENDER
  return (
    <Main>
      <Title title="Sort Dealer Finance Products" className="mb-4" />
      <Buttons order={order} />
      <p className="large mb-2">Re-order AM Finance products by using mouse to drag items</p>
      <Table.DnD
        columns={columns}
        loading={loading}
        result={data}
        onRowDrop={handleRowDrop}
      />
    </Main>
  );
};
// EXPORT
export default LenderOrder;

function Buttons({ order, className }) {
  const navigate = useNavigate();

  const backPath = makePath(INTERFACE.settings, SETTINGS.amFinance, AM_FINANCE.products);

  const handleSave = () => {
    apiFetch({
      method: 'PUT',
      endpoint: ENDPOINTS.admin.amFinance.products.order,
      params: {
        aryDealerAMFinanceProduct: order
      },
      loadingMessage: 'Saving product order.',
      successMessage: 'Product order saved.',
      errorMessage: 'Unable to save product order.',
      onSuccess: () => navigate(backPath)
    });
  };

  return (
    <PageButtons className={clsx('mb-4', className)}>
      <Button.Save label="Save Order" variant="success" className="me-3" size="sm" onClick={handleSave} />
      <Button.Cancel size="sm" onClick={() => navigate(backPath)} /> 
    </PageButtons>
  );
}
